import _ from 'lodash';
import { createSelector } from 'reselect';
import RefData from 'ref-data';
import Immutable from 'seamless-immutable';

import { publishingWizardModes } from '../../common/enums';
import { Channel, DisplayContainer, DisplayUnit, SmartUrl, PublishingDestinations } from '..';
import ChannelSelectors from '../channel/channel-selectors';
import DisplayContainerSelectors from '../display-container/display-container-selectors';
import DisplayUnitSelectors from '../display-unit/display-unit-selectors';
import SmartUrlSelectors from '../smart-url/smart-url-selectors';
import WorkspaceSelectors from '../workspace/workspace-selectors';
import PinnedContainerSelectors from '../pinned-container/pinned-container-selectors';

const FILTER_CHANNELS_BY_IS_STAGING = 'filterByIsStaging';
const FILTER_CHANNELS_BY_IS_LIVE = 'filterByIsLive';

const _scopedState = (state) => state.entities.publishingDestinations;

// ######################################################################################## //
// INPUT SELECTORS
// ######################################################################################## //

const _allIds = (state) => _scopedState(state).allIds;

const _removingIds = (state) => _scopedState(state).removingIds;

const getActiveTab = (state) => _scopedState(state).activeTab;

const isEditorVisible = (state) => _scopedState(state).isEditorVisible;

const isInitialLoad = (state) => _scopedState(state).initialLoad;

const isLoading = (state) => _scopedState(state).isLoading;

const hasFetched = (state) => _scopedState(state).hasFetched;

// ######################################################################################## //
// PUBLISHING DESTINATION SELECTORS
// ######################################################################################## //

//If you run into issues where publishing destinations are not updating without a refresh then this selector may need to be reworked (lifehackett)
const publishingDestinations = createSelector(
  DisplayContainerSelectors.displayContainers,
  ChannelSelectors.channels,
  DisplayUnitSelectors.displayUnits,
  SmartUrlSelectors.smartUrls,
  _allIds,
  _removingIds,
  (displayContainers, channels, displayUnits, smartUrls, publishingDestinationIds, removingIds) => {
    const hydratedPublishingDestinations = _.map(publishingDestinationIds, (pdId) => {
      const hydratedId = JSON.parse(pdId);
      return PublishingDestinations.Model.create({
        channel:          Channel.Collection.findById(hydratedId.channelId, channels),
        displayContainer: DisplayContainer.Collection.findById(hydratedId.displayContainerId, displayContainers),
        displayUnit:      DisplayUnit.Collection.findById(hydratedId.displayUnitId, displayUnits),
        smartUrl:         SmartUrl.Collection.findById(hydratedId.smartUrlId, smartUrls),
        isRemoving:       _.some(removingIds, (removingId) => removingId === pdId),
      });
    });
    return hydratedPublishingDestinations;
  }
);

const livePublishingDestinations = createSelector([publishingDestinations], (publishingDestinations) =>
  publishingDestinations.filter((destination) => !destination.channel.isStaging)
);

const testPublishingDestinations = createSelector([publishingDestinations], (publishingDestinations) =>
  publishingDestinations.filter((destination) => destination.channel.isStaging)
);

const activePublishingDestination = (state) => _scopedState(state).activePublishingDestination;

const isOverlayModalVisible = (state) => _scopedState(state).isOverlayModalVisible;

const overlayContainer = (state) => _scopedState(state).overlayContainer;

const isEditorConfigVisible = (state) => _scopedState(state).isEditorConfigVisible;

const arePublishingCodePropsDirty = createSelector(
  [activePublishingDestination, publishingDestinations],
  (activePublishingDestination, publishingDestinations) => {
    let originalPublishDestination = PublishingDestinations.Collection.findById(
      activePublishingDestination.id,
      publishingDestinations
    );
    const _omitDirtyProps = ['privacyMgmt', 'privacyConfig', 'lastUpdated'];

    const publishingDestination = activePublishingDestination.set(
      'displayContainer',
      _.omit(activePublishingDestination.displayContainer, _omitDirtyProps)
    );
    originalPublishDestination = originalPublishDestination.set(
      'displayContainer',
      _.omit(originalPublishDestination.displayContainer, _omitDirtyProps)
    );

    return !_.isEqual(publishingDestination, originalPublishDestination);
  }
);

// ######################################################################################## //
// WIZARD/EDITOR SELECTORS
// ######################################################################################## //

//Selector for the locally configured list of ALL supported publishingFormats (check base-config.js for more details about the list)
const _publishFormatTypes = (state) => WorkspaceSelectors.activeWorkspace(state).publishFormatTypes;

const _selectedChannelId = (state) => _scopedState(state).data.selectedChannelId;

const _selectedDisplayUnitId = (state) => _scopedState(state).data.selectedDisplayUnitId;

const _selectedDestinationTypeId = (state) => _scopedState(state).data.selectedDestinationTypeId;

const expiryDate = (state) => _scopedState(state).data.expiryDate;

const publishDate = (state) => _scopedState(state).data.publishDate;

const publishingDestinationForWizard = (state) => _scopedState(state).publishingDestination;

const isSaving = (state) => _scopedState(state).isSaving;

const isSaveSuccess = (state) => _scopedState(state).isSaveSuccess;

const isSaveError = (state) => _scopedState(state).isSaveError;

const wizardMode = (state) => _scopedState(state).mode;

const dimensions = (state) => _scopedState(state).data.dimensions;

const privacyMgmt = (state) => _scopedState(state).data.privacyMgmt;

const privacyConfig = (state) => _scopedState(state).data.privacyConfig;

const selectLightboxConfig = (state) => _scopedState(state).data.lightboxConfig;

const selectedChannel = createSelector(_selectedChannelId, ChannelSelectors.channels, (selectedChannelId, channels) =>
  Channel.Collection.findById(selectedChannelId, channels)
);

const selectedDisplayUnit = createSelector(
  _selectedDisplayUnitId,
  DisplayUnitSelectors.displayUnits,
  (selectedDisplayUnitId, displayUnits) => DisplayUnit.Collection.findById(selectedDisplayUnitId, displayUnits)
);

const selectedPinnedContainer = (state) => _scopedState(state).data.selectedPinnedContainer;

const experienceFormatTypeId = (state) => WorkspaceSelectors.activeWorkspace(state).formatType.id;

const isStaging = (state) => _scopedState(state).data.isStaging;

const selectedDestinationType = createSelector(_selectedDestinationTypeId, (selectedDestinationTypeId) => {
  return selectedDestinationTypeId ? RefData.Model.hydrate(selectedDestinationTypeId) : {};
});

//We take the list of supported format types and the selected channel and try to return what format type that channel is
//We return undefined if we were not able to find the formatType for the channel
//NOTE: You should never be in the scenario where a channel does not have a supported formatType !
const selectedFormatType = createSelector(
  selectedChannel,
  _publishFormatTypes,
  selectedDestinationType,
  (channel, formatTypes, selectedDestination) => {
    //Since microsite and web embed have the same channel typeRef (type.channel.web), we also have to make sure
    //that correct display container is selected by comparing the format type code and a destination container type
    return (
      formatTypes.find(
        (formatType) =>
          formatType.channel === channel.typeRef.id && formatType.code === selectedDestination.containerType
      ) || {}
    );
  }
);

const channelsForSelectedDestinationType = createSelector(
  selectedDestinationType,
  ChannelSelectors.channels,
  isStaging,
  (destinationType, channels, isStaging) =>
    Channel.Collection[isStaging ? FILTER_CHANNELS_BY_IS_STAGING : FILTER_CHANNELS_BY_IS_LIVE](
      Channel.Collection.filterByDestinationType(destinationType, channels)
    )
);

// channels selector for each item in the Publishing destinations list
const channelsForPublishingDestinationItem = createSelector(
  [
    ChannelSelectors.channels,
    (state, publishingDestinationType) => publishingDestinationType,
    (state, publishingDestinationType, isStaging) => isStaging,
  ],
  (channels, publishingDestinationType, isStaging) =>
    Channel.Collection[isStaging ? FILTER_CHANNELS_BY_IS_STAGING : FILTER_CHANNELS_BY_IS_LIVE](
      Channel.Collection.filterByDestinationType(publishingDestinationType, channels)
    )
);

const displayUnitsForSelectedDestinationType = createSelector(
  selectedChannel,
  selectedDestinationType,
  DisplayUnitSelectors.displayUnits,
  experienceFormatTypeId,
  (selectedChannel, selectedDestinationType, displayUnits, experienceFormatTypeId) => {
    return _.isEmpty(selectedChannel)
      ? Immutable([])
      : DisplayUnit.Collection.filterByFormatIdAndDestinationType(
        { destinationType: selectedDestinationType, experienceFormatTypeId },
        displayUnits
      );
  }
);

//Grabs the dedicated display unit for a specific channel ID
//Returns the entire display unit
const displayUnitForChannelId = createSelector(
  ChannelSelectors.channelById,
  DisplayUnitSelectors.displayUnits,
  (channel, displayUnits) => displayUnits.find((displayUnit) => displayUnit.typeRef.channel === channel.typeRef.id)
);

const pinnedContainersForSelectedFormatType = createSelector(
  selectedFormatType,
  PinnedContainerSelectors.pinnedContainers,
  (selectedFormatType, pinnedContainers) => {
    const { id: displayContainerId, channel: channelType } = selectedFormatType;
    return channelType === RefData.Enum.TYPE.CHANNEL.WEB
      ? _.filter(pinnedContainers, (container) => container.typeRef.id === displayContainerId)
      : [];
  }
);

const isInnetworkDestinationType = createSelector(
  selectedDestinationType,
  (selectedDestinationType) => selectedDestinationType.id === RefData.Enum.TYPE.DESTINATION.INSTAGRAM
);

const isWizardVisible = (state) => wizardMode(state) !== publishingWizardModes.HIDDEN;

export default {
  getActiveTab,
  isEditorVisible,
  isInitialLoad,
  isLoading,
  hasFetched,
  isOverlayModalVisible,
  overlayContainer,
  isEditorConfigVisible,
  activePublishingDestination,
  arePublishingCodePropsDirty,
  publishingDestinations,
  livePublishingDestinations,
  testPublishingDestinations,

  publishDate,
  expiryDate,
  isSaving,
  isSaveSuccess,
  isSaveError,
  publishingDestinationForWizard,
  wizardMode,
  experienceFormatTypeId,
  selectedChannel,
  selectedDisplayUnit,
  selectedPinnedContainer,
  selectedFormatType,
  selectedDestinationType,
  channelsForSelectedDestinationType,
  channelsForPublishingDestinationItem,
  displayUnitsForSelectedDestinationType,
  isWizardVisible,
  dimensions,
  privacyMgmt,
  privacyConfig,
  selectLightboxConfig,
  displayUnitForChannelId,
  pinnedContainersForSelectedFormatType,
  isStaging,
  isInnetworkDestinationType,
};
